import React, { useState, useCallback } from "react";

import { useRecommendationStore } from "../../stores/recommendationStore";
import { useDocumentGeneratorStore } from "../../stores/documentGeneratorStore";
import { useAuth } from "hooks/useAuth";
import RecommendationTableHeader from "./RecommendationTableHeader";
import RecommendationTableRow from "./RecommendationTableRow";
import WholesaleMarkupModal from "./WholesaleMarkupModal";
import fieldMappers from "../../utils/fieldMappers";
import { ROZETKA_WAREHOUSE_ID } from "../../utils/warehouses";
import { ImageModal } from "components";

// Компонент таблиці рекомендованих цін
const RecommendationTable = React.memo(() => {
  // Отримуємо дані та дії зі стору рекомендацій
  const stocks = useRecommendationStore((state) => state.stocks);
  const warehouse_id = useRecommendationStore((state) => state.filters.warehouse_id);
  const priceViewMode = useRecommendationStore(
    (state) => state.viewSettings.priceViewMode,
  );
  const optimisticUpdateRecommendation = useRecommendationStore(
    (state) => state.optimisticUpdateRecommendation,
  );
  const optimisticUpdateComment = useRecommendationStore(
    (state) => state.optimisticUpdateComment,
  );
  const deleteRecommendation = useRecommendationStore(
    (state) => state.deleteRecommendation,
  );
  const updateWholesaleMarkups = useRecommendationStore(
    (state) => state.updateWholesaleMarkups,
  );
  // Отримуємо дані зі стору призначення цін
  const isSelectionModeActive = useDocumentGeneratorStore().isSelectionModeActive;

  const { isAllowed } = useAuth();
  const [editing, setEditing] = useState({ article: null, field: null, value: "" });
  const [selectedImage, setSelectedImage] = useState(null);
  // Товар, для якого відкрито модалку оптових націнок
  const [markupItem, setMarkupItem] = useState(null);
  const isRozetka = warehouse_id === ROZETKA_WAREHOUSE_ID;

  // Права на редагування
  const canEditPrices = isAllowed("recommendations:edit-prices");
  const canEditComments = isAllowed("recommendations:edit-comments");

  // Обробник кліку по мініатюрі зображення
  const handleImageClick = useCallback((imgUrl) => {
    if (!imgUrl) return;
    setSelectedImage(imgUrl);
  }, []);

  // Закриття модального вікна зображення
  const closeImageModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  // Функція для ініціалізації редагування комірки
  const startEditing = useCallback(
    (item, field) => {
      if (field === "comment" ? canEditComments : canEditPrices) {
        const initialValue = item[field] !== null ? String(item[field]) : "";
        setEditing({
          article: item.article,
          field,
          value: initialValue,
        });
      }
    },
    [canEditComments, canEditPrices],
  );

  const cancelEditing = useCallback(() => {
    setEditing({ article: null, field: null, value: "" });
  }, []);

  // Змінюємо стан value в полі редагування
  const handleEditChange = useCallback((e) => {
    setEditing((prev) => ({ ...prev, value: e.target.value }));
  }, []);

  // Функція для завершення редагування та відправки оновлення
  const handleUpdate = useCallback(() => {
    const { article, field, value } = editing;

    if (field === "comment") {
      if (!canEditComments) return;
      optimisticUpdateComment(warehouse_id, article, value);
    } else {
      if (!canEditPrices) return;
      const backendField = fieldMappers(field, "toBackend");
      if (backendField) {
        const newValue = value !== "" ? parseFloat(value) : null;
        optimisticUpdateRecommendation(warehouse_id, article, {
          [backendField]: newValue,
        });
      }
    }
    cancelEditing();
  }, [
    editing,
    canEditPrices,
    canEditComments,
    warehouse_id,
    optimisticUpdateRecommendation,
    optimisticUpdateComment,
    cancelEditing,
  ]);

  // Функція для визначення динамічної колонки цін
  const getDynamicPriceHeader = () => {
    switch (priceViewMode) {
      case "price_wholesale":
        return {
          label: "Опт (2,5%)",
          field: "price_wholesale",
          requiredPermission: "recommendations:view-wholesale",
        };
      case "price_birthday":
        return {
          label: "Ювіляри (1%)",
          field: "price_birthday",
        };
      case "price_05":
      default:
        return {
          label: "Військові (1%)",
          field: "price_05",
        };
    }
  };

  // Конфігурація заголовків таблиці
  let headers = [
    { label: "Артикул", field: "article" },
    { label: "Фото", field: null },
    { label: "Назва товару", field: "name" },
    // У розділі Rozetka окремо видно залишки буферного складу Rozetka
    ...(isRozetka ? [{ label: "Залишки Rozetka", field: null }] : []),
    { label: "Залишки продаж", field: "quantity" },
    {
      label: "Залишки загальні",
      field: "quantity_total",
      requiredPermission: "recommendations:view-stocks",
    },
    // У розділі Rozetka лише базова ціна, комісія категорії та фінальна ціна
    ...(isRozetka
      ? [
          { label: "Ціна без комісії", field: null },
          { label: "Комісія", field: null },
          { label: "Ціна Rozetka", field: "price" },
        ]
      : [
          { label: "РРЦ (1%)", field: "price" },
          { label: "Знижка (0,75%)", field: "price_11" },
          { label: "min (0,5%)", field: "price_custom" },
          getDynamicPriceHeader(),
        ]),
    { label: "Тип ціни", field: null },
    { label: "Фактична вага", field: "actual_weight" },
    { label: "Об'ємна вага", field: "volumetric_weight" },
    {
      label: "Дії",
      field: null,
      requiredPermission: [
        "recommendations:edit-prices",
        "recommendations:delete-prices",
        "recommendations:edit-custom-tags",
      ],
    },
  ];

  headers = isSelectionModeActive
    ? [
      {
        label: "",
        field: null,
        className: "selection-column",
      },
      ...headers,
    ]
    : headers;

  // Рахуємо лише ті колонки, які реально видно користувачу
  const visibleHeadersCount = headers.filter(
    ({ requiredPermission }) => !requiredPermission || isAllowed(requiredPermission),
  ).length;

  return (
    <>
      <table id="recommendation-table">
        <RecommendationTableHeader headers={headers} />
        <tbody>
          {stocks.length === 0 ? (
            <tr>
              <td colSpan={visibleHeadersCount} className="empty-row">
                Немає даних товарів рекомендацій
              </td>
            </tr>
          ) : (
            stocks.map((item) => (
              <RecommendationTableRow
                key={item.article}
                item={item}
                editing={editing}
                canEditPrices={canEditPrices}
                canEditComments={canEditComments}
                onImageClick={handleImageClick}
                onStartEditing={startEditing}
                onUpdateValue={handleUpdate}
                onEditChange={handleEditChange}
                onCancelEditing={cancelEditing}
                onDelete={() => deleteRecommendation(warehouse_id, item.article)}
                onEditWholesaleMarkups={setMarkupItem}
              />
            ))
          )}
        </tbody>
      </table>
      <ImageModal
        isOpen={!!selectedImage}
        src={selectedImage}
        alt="Enlarged"
        onClose={closeImageModal}
      />
      {markupItem && (
        <WholesaleMarkupModal
          item={markupItem}
          onSave={updateWholesaleMarkups}
          onClose={() => setMarkupItem(null)}
        />
      )}
    </>
  );
});

export default RecommendationTable;
